import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, Spinner } from "../components/ui";
import StoragePanel from "../components/StoragePanel";
import { formatBytes } from "../components/drive/driveUtils";
import { cn } from "../lib/utils";
import { Users, Building2, CreditCard, TrendingUp, Sparkles, Search, ShieldCheck, RefreshCw, Trash2, ShieldAlert, Check } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);
const PLAN_COLOR = {
  free: "bg-surface-hover text-text-secondary",
  trial: "bg-amber-500/15 text-amber-400",
  locked: "bg-red-500/15 text-red-400",
  pro: "bg-blue-500/15 text-blue-400",
  business: "bg-primary/15 text-primary",
  portfolio: "bg-emerald-500/15 text-emerald-400",
  enterprise: "bg-amber-500/15 text-amber-400",
  beta: "bg-fuchsia-500/15 text-fuchsia-400",
  admin: "bg-amber-500/15 text-amber-400",
};

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="flex items-center gap-4" data-testid={testid}>
      <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center shrink-0", accent)}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-heading font-black text-2xl text-text-primary tabular-nums">{value}</div>
        <div className="text-xs text-text-secondary">{label}</div>
      </div>
    </Card>
  );
}

export default function Admin() {
  const { user, lang, toast } = useApp();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [reseedConfirm, setReseedConfirm] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [showPurge, setShowPurge] = useState(false);
  const [purgeKeepDemo, setPurgeKeepDemo] = useState(true);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  const loadUsers = useCallback(async (q = "") => {
    const { data } = await api.get("/admin/users", { params: { search: q } });
    setUsers(data.users || []);
  }, []);

  const reseedDemo = async () => {
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/reseed-demo");
      toast(L(lang, `Démo réinitialisée (${data.entities} entités, données du mois en cours).`,
                    `Demo re-seeded (${data.entities} entities, current-month data).`), { variant: "success" });
      setReseedConfirm(false);
      const { data: s } = await api.get("/admin/stats");
      setStats(s);
    } catch {
      toast(L(lang, "Échec de la réinitialisation de la démo.", "Demo re-seed failed."), { variant: "error" });
    } finally {
      setReseeding(false);
    }
  };

  const deleteUser = async (u) => {
    setDeletingId(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast(L(lang, "Utilisateur supprimé.", "User deleted."), { variant: "success" });
      setConfirmDeleteId("");
      await loadUsers(search);
      const { data: s } = await api.get("/admin/stats"); setStats(s);
    } catch (e) {
      toast(e?.response?.data?.detail || L(lang, "Échec de la suppression.", "Delete failed."), { variant: "error" });
    } finally { setDeletingId(""); }
  };

  const runPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/users/purge", { keep_demo: purgeKeepDemo, confirm: purgeConfirm });
      toast(L(lang, `${data.deleted} utilisateur(s) supprimé(s), ${data.kept} conservé(s).`, `${data.deleted} user(s) deleted, ${data.kept} kept.`), { variant: "success" });
      setShowPurge(false); setPurgeConfirm("");
      await loadUsers("");
      const { data: s } = await api.get("/admin/stats"); setStats(s);
    } catch (e) {
      toast(e?.response?.data?.detail || L(lang, "Échec de la purge.", "Purge failed."), { variant: "error" });
    } finally { setPurging(false); }
  };

  useEffect(() => {
    if (!user?.is_platform_admin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
        await loadUsers("");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, loadUsers]);

  if (!user) return null;
  if (!user.is_platform_admin) return <Navigate to="/" replace />;

  const toggleBeta = async (u) => {
    setBusy(u.user_id);
    try {
      const next = !u.is_beta;
      await api.post(`/admin/users/${u.user_id}/beta`, { beta: next });
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id
        ? { ...x, is_beta: next, plan: x.is_platform_admin ? "admin" : (next ? "beta" : x.raw_plan) } : x));
    } finally {
      setBusy("");
    }
  };

  const maxSignup = Math.max(1, ...(stats?.signups || []).map((s) => s.count));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-amber-400" />
          <h1 className="text-3xl font-heading font-medium tracking-tight">{L(lang, "Administration", "Admin")}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {reseedConfirm ? (
            <>
              <span className="text-xs text-text-secondary hidden sm:inline">{L(lang, "Recharger la démo ?", "Re-seed demo?")}</span>
              <button onClick={reseedDemo} disabled={reseeding} data-testid="confirm-reseed-demo"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-60">
                <RefreshCw className={cn("w-3.5 h-3.5", reseeding && "animate-spin")} />
                {reseeding ? L(lang, "Rechargement…", "Re-seeding…") : L(lang, "Confirmer", "Confirm")}
              </button>
              <button onClick={() => setReseedConfirm(false)} disabled={reseeding} data-testid="cancel-reseed-demo"
                className="px-3 py-1.5 rounded-lg border border-border text-text-secondary text-sm hover:text-text-primary transition-colors">
                {L(lang, "Annuler", "Cancel")}
              </button>
            </>
          ) : (
            <button onClick={() => setReseedConfirm(true)} data-testid="reseed-demo-button"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-border text-sm font-medium text-text-primary hover:bg-surface-hover transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> {L(lang, "Recharger la démo", "Re-seed demo")}
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-text-secondary mb-8">{L(lang, "Vue plateforme, réservée aux super-admins.", "Platform overview, super-admins only.")}</p>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-8 h-8 text-text-secondary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard testid="admin-stat-users" icon={Users} label={L(lang, "Utilisateurs", "Users")} value={stats.totals.users} accent="bg-primary/15 text-primary" />
            <StatCard testid="admin-stat-workspaces" icon={Building2} label={L(lang, "Espaces de travail", "Workspaces")} value={stats.totals.workspaces} accent="bg-blue-500/15 text-blue-400" />
            <StatCard testid="admin-stat-subs" icon={CreditCard} label={L(lang, "Abonnements actifs", "Active subs")} value={stats.subscriptions.active_paid} accent="bg-emerald-500/15 text-emerald-400" />
            <StatCard testid="admin-stat-mrr" icon={TrendingUp} label={L(lang, "MRR", "MRR")} value={`€${stats.subscriptions.mrr}`} accent="bg-emerald-500/15 text-emerald-400" />
            <StatCard testid="admin-stat-beta" icon={Sparkles} label={L(lang, "Comptes beta", "Beta accounts")} value={stats.totals.beta_users} accent="bg-fuchsia-500/15 text-fuchsia-400" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-8">
            <Card className="lg:col-span-2" data-testid="admin-signups-chart">
              <SectionLabel>{L(lang, "Inscriptions (12 semaines)", "Signups (12 weeks)")}</SectionLabel>
              <div className="flex items-end gap-2 h-40 mt-4">
                {stats.signups.map((s) => (
                  <div key={s.week} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${s.week}: ${s.count}`}>
                    <span className="text-[10px] text-text-secondary tabular-nums">{s.count || ""}</span>
                    <div className="w-full rounded-t bg-primary/80 transition-all" style={{ height: `${(s.count / maxSignup) * 100}%`, minHeight: s.count ? 4 : 0 }} />
                    <span className="text-[8px] text-text-secondary">{s.week.slice(5)}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card data-testid="admin-plan-distribution">
              <SectionLabel>{L(lang, "Répartition des plans", "Plan distribution")}</SectionLabel>
              <div className="space-y-2.5 mt-4">
                {Object.entries(stats.plan_distribution).map(([plan, n]) => (
                  <div key={plan} className="flex items-center justify-between text-sm">
                    <span className={cn("px-2 py-0.5 rounded text-xs font-medium capitalize", PLAN_COLOR[plan] || PLAN_COLOR.free)}>{plan}</span>
                    <span className="tabular-nums text-text-primary font-medium">{n}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <StoragePanel />

          <Card data-testid="admin-users-table">
            <div className="flex items-center justify-between gap-4 mb-4">
              <SectionLabel>{L(lang, "Utilisateurs", "Users")}</SectionLabel>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadUsers(search)}
                    placeholder={L(lang, "Rechercher email / nom…", "Search email / name…")}
                    data-testid="admin-user-search"
                    className="pl-9 pr-3 py-2 rounded-lg bg-surface-hover border border-border text-sm text-text-primary w-64 focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <button onClick={() => setShowPurge(true)} data-testid="admin-purge-button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/40 text-red-400 text-sm font-medium hover:bg-red-500/10 transition-colors whitespace-nowrap">
                  <ShieldAlert className="w-4 h-4" /> {L(lang, "Purger", "Purge")}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="text-left text-text-secondary border-b border-border">
                    <th className="py-2 pr-3 font-medium">{L(lang, "Utilisateur", "User")}</th>
                    <th className="py-2 px-3 font-medium">{L(lang, "Plan", "Plan")}</th>
                    <th className="py-2 px-3 font-medium text-center">{L(lang, "Vérifié", "Verified")}</th>
                    <th className="py-2 px-3 font-medium text-center">{L(lang, "Entités", "Entities")}</th>
                    <th className="py-2 px-3 font-medium text-right">{L(lang, "Stockage", "Storage")}</th>
                    <th className="py-2 px-3 font-medium">{L(lang, "Inscrit", "Joined")}</th>
                    <th className="py-2 px-3 font-medium text-right">Beta</th>
                    <th className="py-2 pl-3 font-medium text-right">{L(lang, "Action", "Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-border/50 last:border-0" data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-text-primary font-medium">{u.email}</div>
                        {u.name && <div className="text-xs text-text-secondary">{u.name}</div>}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={cn("px-2 py-0.5 rounded text-xs font-medium capitalize", PLAN_COLOR[u.plan] || PLAN_COLOR.free)}>{u.plan}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {u.email_verified
                          ? <Check className="w-4 h-4 mx-auto text-emerald-400" />
                          : <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{L(lang, "Non", "No")}</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center tabular-nums text-text-primary">{u.entities}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-text-secondary">{formatBytes(u.storage_used_bytes || 0)}</td>
                      <td className="py-2.5 px-3 text-text-secondary text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { year: "2-digit", month: "short", day: "numeric" }) : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {u.is_platform_admin ? (
                          <span className="text-xs text-text-secondary">{L(lang, "Admin", "Admin")}</span>
                        ) : (
                          <button onClick={() => toggleBeta(u)} disabled={busy === u.user_id}
                            data-testid={`admin-beta-toggle-${u.user_id}`}
                            className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                              u.is_beta ? "bg-fuchsia-500" : "bg-surface-hover border border-border")}>
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", u.is_beta ? "translate-x-4" : "translate-x-0.5")} />
                          </button>
                        )}
                      </td>
                      <td className="py-2.5 pl-3 text-right">
                        {u.is_platform_admin ? (
                          <span className="text-xs text-text-secondary">—</span>
                        ) : confirmDeleteId === u.user_id ? (
                          <span className="inline-flex items-center gap-1">
                            <button onClick={() => deleteUser(u)} disabled={deletingId === u.user_id} data-testid={`admin-confirm-delete-${u.user_id}`}
                              className="px-2 py-1 rounded bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-60">
                              {deletingId === u.user_id ? "…" : L(lang, "Oui", "Yes")}
                            </button>
                            <button onClick={() => setConfirmDeleteId("")} className="px-2 py-1 rounded border border-border text-xs text-text-secondary">
                              {L(lang, "Non", "No")}
                            </button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDeleteId(u.user_id)} data-testid={`admin-delete-${u.user_id}`}
                            className="p-1.5 rounded hover:bg-red-500/10 text-text-secondary hover:text-red-400 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-text-secondary">{L(lang, "Aucun utilisateur.", "No users.")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-text-secondary mt-3">
              {L(lang, "Activer « Beta » donne à un utilisateur l'accès complet du plan Portfolio, gratuitement.",
                       "Turning on “Beta” gives a user full Portfolio-level access, for free.")}
            </p>
          </Card>

          {showPurge && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-purge-modal">
              <Card className="w-full max-w-md">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <ShieldAlert className="w-5 h-5" />
                  <h3 className="font-heading font-bold text-lg text-text-primary">{L(lang, "Purger les comptes", "Purge accounts")}</h3>
                </div>
                <p className="text-sm text-text-secondary mb-4">
                  {L(lang, "Cela supprime DÉFINITIVEMENT tous les utilisateurs non-admin et toutes leurs données. Les super-admins sont toujours conservés. Action irréversible.",
                           "This PERMANENTLY deletes every non-admin user and all their data. Platform admins are always kept. This cannot be undone.")}
                </p>
                <label className="flex items-center gap-2 text-sm text-text-primary mb-4">
                  <input type="checkbox" checked={purgeKeepDemo} onChange={(e) => setPurgeKeepDemo(e.target.checked)} data-testid="admin-purge-keep-demo" />
                  {L(lang, "Conserver le compte démo (demo@cockpit.test)", "Keep the demo account (demo@cockpit.test)")}
                </label>
                <p className="text-xs text-text-secondary mb-1">{L(lang, "Tapez DELETE pour confirmer", "Type DELETE to confirm")}</p>
                <input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} data-testid="admin-purge-confirm-input"
                  className="w-full mb-4 px-3 py-2 rounded-lg bg-surface-hover border border-border text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-red-500" placeholder="DELETE" />
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => { setShowPurge(false); setPurgeConfirm(""); }} className="px-3 py-2 rounded-lg border border-border text-sm text-text-secondary">
                    {L(lang, "Annuler", "Cancel")}
                  </button>
                  <button onClick={runPurge} disabled={purging || purgeConfirm.trim().toUpperCase() !== "DELETE"} data-testid="admin-purge-execute"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
                    {purging ? "…" : L(lang, "Supprimer définitivement", "Delete permanently")}
                  </button>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
